import './InternalSecondaryMobile.css';
// src/MaintenanceContractRevenue.js
//
// Contract Revenue — what our maintenance contracts bill, and how much
// additional work they pull through. L3-only (CAT_SERVICE).
//
// The number this page exists to produce is the leverage ratio: for every
// dollar of maintenance contract billing, how many dollars of in-suite
// and mechanical work does the portfolio generate? That's the case for
// the dashboard paying for itself.
//
// Everything here is actual invoiced revenue from Jobber, never the
// stored contract prices — only about half the contracts have a price
// recorded, so they can't carry a revenue figure.
//
// Add-on work is attributed to a building by Jobber client link first,
// then by billing address, because most in-suite repairs are billed to
// the resident rather than the corporation. Revenue at buildings we hold
// no contract at is excluded entirely — it says nothing about contract ROI.

import React, { useEffect, useMemo, useState } from 'react';
import {
  FaBuilding, FaChartLine, FaFileContract,
  FaSearch, FaSort, FaSortDown, FaSortUp, FaSyncAlt, FaTools,
} from 'react-icons/fa';
import API_BASE_URL from './config/api';
import { cacheGet, cachePut, cacheAgeMs, formatAge } from './utils/staleCache';

const C = {
  navy: '#1F3A68',
  ink: '#1a2236',
  inkSoft: '#374151',
  muted: '#6b7280',
  line: '#E6E8EC',
  lineSoft: '#EFF2F7',
  bg: '#F4F7FB',
  card: '#ffffff',
  green: '#1c9550',
  greenSoft: '#E3F5EA',
  amber: '#b45309',
  amberSoft: '#FEF3C7',
  blue: '#2563eb',
  blueSoft: '#eff6ff',
  purple: '#6d28d9',
  purpleSoft: '#F3EEFF',
  shadow: '0 1px 3px rgba(20,30,60,.06), 0 8px 24px rgba(20,30,60,.04)',
};

const sharedFont =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif';

// Jobber holds no invoices before this, so it's where "all time" starts
// and the floor for every other window.
const JOBBER_EPOCH = '2026-01-01';

const ROLLING_MONTHS = [12, 6, 3];

// How in-suite add-on work gets tied back to a maintenance visit.
const WINDOWS = [
  { key: 'all', label: 'All work in period', field: 'addonInsuite' },
  { key: '60', label: 'Within 60 days of a cycle', field: 'addonInsuite60' },
  { key: '90', label: 'Within 90 days of a cycle', field: 'addonInsuite90' },
];

// Which add-on stream counts toward the leverage ratio.
const ADDON_MODES = [
  { key: 'both', label: 'Both' },
  { key: 'insuite', label: 'In-suite' },
  { key: 'mech', label: 'Mechanical' },
];

const CONTRACT_LABELS = {
  'hvac-maintenance': 'Fan coil',
  'dryer-vent': 'Dryer vent',
  'mechanical-room': 'Mechanical',
};

const money = (n, dec = 0) => `$${Number(n || 0).toLocaleString('en-CA', {
  minimumFractionDigits: dec, maximumFractionDigits: dec,
})}`;

// Both sides of the ratio have to come from the same stream, or the
// number is meaningless: in-suite add-on measured against in-suite
// contract billing, mechanical against mechanical.
function counted(insuite, mech, mode) {
  if (mode === 'insuite') return insuite;
  if (mode === 'mech') return mech;
  return insuite + mech;
}

function localIso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Matches the Seasonal Planner exactly. The two cycles partition the
// calendar (Spring = Jan–Aug, Fall = Sep–Dec), which is what guarantees an
// invoice lands in one cycle and one only — none counted twice, none lost.
function seasonWindow(name, year) {
  return name === 'Fall'
    ? { from: `${year}-09-01`, to: `${year}-12-31` }
    : { from: `${year}-01-01`, to: `${year}-08-31` };
}

// All time, the rolling windows, then every cycle that has actually
// started. Cycles are listed newest first so the useful ones sit nearest
// the default.
function periodOptions(today = new Date()) {
  const todayIso = localIso(today);
  const opts = [{ key: 'all', label: 'All time' }];
  ROLLING_MONTHS.forEach((m) => opts.push({ key: `m${m}`, label: `Last ${m} months` }));

  const seasons = [];
  for (let year = Number(JOBBER_EPOCH.slice(0, 4)); year <= today.getFullYear(); year += 1) {
    ['Spring', 'Fall'].forEach((name) => {
      if (seasonWindow(name, year).from <= todayIso) {
        seasons.push({ key: `${name.toLowerCase()}-${year}`, label: `${name} ${year}` });
      }
    });
  }
  return opts.concat(seasons.reverse());
}

function periodRange(key, today = new Date()) {
  const todayIso = localIso(today);
  if (key === 'all') return { from: JOBBER_EPOCH, to: todayIso };

  if (key.startsWith('m')) {
    const months = Number(key.slice(1));
    const from = new Date(today.getFullYear(), today.getMonth() - months, today.getDate());
    return { from: localIso(from), to: todayIso };
  }

  const [name, year] = key.split('-');
  const win = seasonWindow(name === 'fall' ? 'Fall' : 'Spring', Number(year));
  // A cycle in progress has no data past today; asking for the rest of it
  // would just widen the crawl for nothing.
  return { from: win.from, to: win.to < todayIso ? win.to : todayIso };
}

function prettyDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-CA', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

export default function MaintenanceContractRevenue() {
  const [period, setPeriod] = useState('all');
  const [windowKey, setWindowKey] = useState('all');
  const [addonMode, setAddonMode] = useState('both');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'contractShown', dir: 'desc' });
  const [reloadKey, setReloadKey] = useState(0);
  const [forceFresh, setForceFresh] = useState(false);

  const [loading, setLoading] = useState(false);
  const [revalidating, setRevalidating] = useState(false);
  const [warming, setWarming] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [dataAgeMs, setDataAgeMs] = useState(null);

  const range = useMemo(() => periodRange(period), [period]);
  const periods = useMemo(() => periodOptions(), []);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = `cr:${range.from}:${range.to}`;
    const cached = forceFresh ? null : cacheGet(cacheKey);

    if (cached && cached.value) {
      setData(cached.value);
      setDataAgeMs(cacheAgeMs(cached) + ((cached.value.cacheAgeSeconds || 0) * 1000));
      setLoading(false);
      setError(null);
    } else {
      setData(null);
      setDataAgeMs(null);
      setLoading(true);
      setError(null);
    }

    // The crawl is ~1800 invoices across ~60 pages with rate-limit
    // sleeps, so a cold window takes around three minutes. Poll rather
    // than block the request and eat a Heroku H12.
    let timer = null;
    let polls = 0;
    const MAX_POLLS = 60;
    const POLL_MS = 5000;

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      setRevalidating(false);
      setForceFresh(false);
    };

    const tick = async (isInitial) => {
      setRevalidating(true);
      try {
        const token = localStorage.getItem('managerToken') || '';
        const qs = isInitial && forceFresh ? '&fresh=1' : '';
        const resp = await fetch(
          `${API_BASE_URL}/api/jobber/contract-revenue?from=${range.from}&to=${range.to}${qs}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        );
        const body = await resp.json().catch(() => ({}));
        if (cancelled) return;

        if (!resp.ok) {
          if (!cached) setError({ kind: resp.status === 403 ? 'forbidden' : 'generic', detail: body?.msg || `HTTP ${resp.status}` });
          setWarming(false);
          finish();
          return;
        }

        if (!body?.ok) {
          if (body?.reason === 'warming' && polls < MAX_POLLS) {
            setWarming(true);
            setError(null);
            polls += 1;
            timer = setTimeout(() => tick(false), POLL_MS);
            return;
          }
          if (!cached) {
            setError(body?.reason === 'warming'
              ? { kind: 'warming_timeout' }
              : { kind: body?.reason || 'generic', detail: body?.detail || '' });
          }
          setWarming(false);
          finish();
          return;
        }

        setError(null);
        setWarming(false);
        setData(body);
        cachePut(cacheKey, body);
        setDataAgeMs((body.cacheAgeSeconds || 0) * 1000);
        // There are numbers to show now, so drop the full-page spinner
        // even though the poll loop below may keep running. `revalidating`
        // stays on, which is what the "Updating…" chip reads.
        setLoading(false);
        // ok=true can still be stale while the server refreshes behind
        // it — keep polling so the final numbers land without a click.
        if (body._revalidating && polls < MAX_POLLS) {
          polls += 1;
          timer = setTimeout(() => tick(false), POLL_MS);
          return;
        }
        finish();
      } catch (e) {
        if (cancelled) return;
        if (!cached) setError({ kind: 'generic', detail: e?.message || String(e) });
        setWarming(false);
        finish();
      }
    };
    tick(true);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [range.from, range.to, reloadKey, forceFresh]);

  useEffect(() => {
    if (dataAgeMs == null) return undefined;
    const id = setInterval(() => setDataAgeMs((p) => (p == null ? p : p + 60000)), 60000);
    return () => clearInterval(id);
  }, [dataAgeMs]);

  const windowField = WINDOWS.find((w) => w.key === windowKey)?.field || 'addonInsuite';

  const rows = useMemo(() => {
    const sites = data?.sites || [];
    const query = search.trim().toLowerCase();
    const mapped = sites.map((s) => {
      const insuite = s[windowField] || 0;
      const addon = counted(insuite, s.addonMech || 0, addonMode);
      const contract = counted(s.contractInsuite || 0, s.contractMech || 0, addonMode);
      return {
        ...s,
        addonInsuiteShown: insuite,
        addonTotal: addon,
        contractShown: contract,
        ratio: contract > 0 ? addon / contract : null,
      };
    }).filter((s) => {
      if (!query) return true;
      return `${s.name} ${(s.buildingIds || []).join(' ')}`.toLowerCase().includes(query);
    });

    const dir = sort.dir === 'asc' ? 1 : -1;
    return mapped.sort((a, b) => {
      if (sort.key === 'name') return a.name.localeCompare(b.name) * dir;
      const av = a[sort.key];
      const bv = b[sort.key];
      // Sites with no contract billing have no ratio; park them last
      // regardless of direction so the top of the table is always
      // meaningful.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av - bv) * dir || a.name.localeCompare(b.name);
    });
  }, [data, search, sort, windowField, addonMode]);

  const totals = useMemo(() => {
    const s = data?.summary;
    if (!s) return null;
    const insuite = s[windowField] || 0;
    const mech = s.addonMech || 0;
    const addon = counted(insuite, mech, addonMode);
    const contractInsuite = s.contractInsuite || 0;
    const contractMech = s.contractMech || 0;
    const contract = counted(contractInsuite, contractMech, addonMode);
    return {
      contract,
      contractInsuite,
      contractMech,
      addonInsuite: insuite,
      addonMech: mech,
      addon,
      ratio: contract > 0 ? addon / contract : 0,
      sites: s.siteCount || 0,
      // Mode-aware: in mechanical mode this has to mean "sites that billed
      // a mechanical contract", not "sites that billed anything".
      billingSites: (data.sites || []).filter(
        (x) => counted(x.contractInsuite || 0, x.contractMech || 0, addonMode) > 0,
      ).length,
      units: s.units || 0,
    };
  }, [data, windowField, addonMode]);

  return (
    <div className="internal-secondary-page internal-contract-revenue-page" style={{ padding: '24px 28px', fontFamily: sharedFont, color: C.ink, minHeight: '100vh', background: C.bg }}>
      <style>{'@keyframes cr-spin { to { transform: rotate(360deg); } }'}</style>

      <Header
        period={period}
        periods={periods}
        onPeriod={setPeriod}
        range={range}
        busy={loading || revalidating}
        dataAgeMs={dataAgeMs}
        onRefresh={() => { setForceFresh(true); setReloadKey((k) => k + 1); }}
      />

      {error && <ErrorBanner error={error} />}

      {loading ? (
        <LoadingState warming={warming} />
      ) : !data ? (
        !error && <EmptyState />
      ) : (
        <>
          <HeroRow totals={totals} windowKey={windowKey} addonMode={addonMode} />

          <Toolbar
            windowKey={windowKey}
            onWindow={setWindowKey}
            addonMode={addonMode}
            onAddonMode={setAddonMode}
            search={search}
            onSearch={setSearch}
            shown={rows.length}
            total={(data.sites || []).length}
          />

          <SiteTable
            rows={rows}
            sort={sort}
            onSort={setSort}
            windowKey={windowKey}
            addonMode={addonMode}
          />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function Header({ period, periods, onPeriod, range, busy, dataAgeMs, onRefresh }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
      gap: 16, flexWrap: 'wrap', marginBottom: 18,
    }}>
      <div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: C.navy }}>
          Contract Revenue
        </h1>
        <div style={{ marginTop: 5, color: C.muted, fontSize: 13.5 }}>
          What our maintenance contracts bill, and the work they pull through.
          {' '}
          <span style={{ color: C.inkSoft }}>
            {prettyDate(range.from)} – {prettyDate(range.to)}
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <select
          value={period}
          onChange={(e) => onPeriod(e.target.value)}
          style={{
            background: C.card, border: `1px solid ${C.line}`, borderRadius: 9,
            padding: '9px 13px', fontSize: 12.5, fontWeight: 750,
            fontFamily: 'inherit', color: C.inkSoft, cursor: 'pointer', outline: 'none',
          }}
        >
          {periods.map((p) => (
            <option key={p.key} value={p.key}>{p.label}</option>
          ))}
        </select>

        <button
          type="button"
          onClick={onRefresh}
          disabled={busy}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: C.card, border: `1px solid ${C.line}`, borderRadius: 9,
            padding: '9px 13px', fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit',
            color: busy ? C.muted : C.inkSoft, cursor: busy ? 'default' : 'pointer',
          }}
        >
          <FaSyncAlt style={busy ? { animation: 'cr-spin 0.9s linear infinite' } : undefined} />
          {busy ? 'Updating…' : dataAgeMs != null ? `Updated ${formatAge(dataAgeMs)}` : 'Refresh'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hero metrics
// ---------------------------------------------------------------------------

function HeroRow({ totals, windowKey, addonMode }) {
  if (!totals) return null;
  const insuitePhrase = windowKey === 'all'
    ? 'all in-suite work in the period'
    : `in-suite work within ${windowKey} days of a cycle`;
  const windowNote = addonMode === 'mech'
    ? 'mechanical work against mechanical contracts'
    : addonMode === 'insuite'
      ? `${insuitePhrase}, against in-suite contracts`
      : `${insuitePhrase}, plus mechanical`;
  const addonSub = addonMode === 'both'
    ? `${money(totals.addonInsuite)} in-suite · ${money(totals.addonMech)} mechanical`
    : addonMode === 'insuite'
      ? `In-suite only · ${money(totals.addonMech)} mechanical excluded`
      : `Mechanical only · ${money(totals.addonInsuite)} in-suite excluded`;
  const contractSub = addonMode === 'both'
    ? `${money(totals.contractInsuite)} in-suite · ${money(totals.contractMech)} mechanical`
    : addonMode === 'insuite'
      ? `In-suite only · ${money(totals.contractMech)} mechanical excluded`
      : `Mechanical only · ${money(totals.contractInsuite)} in-suite excluded`;

  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
      <Metric
        icon={<FaFileContract />}
        label="Contract revenue"
        value={money(totals.contract)}
        sub={contractSub}
      />
      <Metric
        icon={<FaTools />}
        label="Add-on work generated"
        value={money(totals.addon)}
        sub={addonSub}
        accent={C.green}
      />
      <Metric
        icon={<FaChartLine />}
        label="Add-on per contract dollar"
        value={`${totals.ratio.toFixed(2)}×`}
        sub={`Counting ${windowNote}`}
        accent={C.purple}
        background={C.purpleSoft}
        highlight
      />
      <Metric
        icon={<FaBuilding />}
        label="Buildings under contract"
        value={String(totals.sites)}
        sub={`${totals.billingSites} billed in period · ${totals.units.toLocaleString('en-CA')} units`}
      />
    </div>
  );
}

function Metric({ icon, label, value, sub, accent = C.navy, background = C.card, highlight }) {
  return (
    <div style={{
      background, border: `1px solid ${highlight ? `${C.purple}33` : C.line}`,
      borderRadius: 12, padding: '16px 20px', boxShadow: C.shadow,
      flex: '1 1 220px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, color: C.muted,
        fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em',
      }}>
        {icon}<span>{label}</span>
      </div>
      <div style={{ fontSize: 29, fontWeight: 800, color: accent, lineHeight: 1.15 }}>{value}</div>
      {sub ? <div style={{ fontSize: 12, color: C.muted }}>{sub}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------

function Toolbar({ windowKey, onWindow, addonMode, onAddonMode, search, onSearch, shown, total }) {
  const label = {
    fontSize: 12, fontWeight: 700, color: C.muted,
    textTransform: 'uppercase', letterSpacing: '.04em',
  };
  const group = {
    display: 'inline-flex', border: `1px solid ${C.line}`,
    borderRadius: 9, overflow: 'hidden',
  };
  const segment = (active) => ({
    border: 0, padding: '8px 13px', fontSize: 12.5, fontWeight: 700,
    cursor: 'pointer', fontFamily: 'inherit',
    background: active ? C.blueSoft : '#fff',
    color: active ? C.blue : C.muted,
  });

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      background: C.card, border: `1px solid ${C.line}`, borderRadius: 11,
      padding: '11px 13px', marginBottom: 14, boxShadow: C.shadow,
    }}>
      <span style={label}>Count</span>
      <div style={group}>
        {ADDON_MODES.map((m) => (
          <button
            key={m.key}
            type="button"
            onClick={() => onAddonMode(m.key)}
            style={segment(addonMode === m.key)}
          >{m.label}</button>
        ))}
      </div>

      {/* The attribution window only moves in-suite figures, so it has
          nothing to say when the ratio is mechanical-only. */}
      {addonMode !== 'mech' && (
        <>
          <span style={label}>Attribute in-suite work</span>
          <div style={group}>
            {WINDOWS.map((w) => (
              <button
                key={w.key}
                type="button"
                onClick={() => onWindow(w.key)}
                style={segment(windowKey === w.key)}
              >{w.label}</button>
            ))}
          </div>
        </>
      )}

      <div style={{ flex: 1 }} />

      <span style={{ fontSize: 12, color: C.muted }}>
        {shown === total ? `${total} buildings` : `${shown} of ${total}`}
      </span>
      <div style={{ position: 'relative' }}>
        <FaSearch style={{
          position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
          color: C.muted, fontSize: 12,
        }} />
        <input
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Search building or ID…"
          style={{
            border: `1px solid ${C.line}`, borderRadius: 8, padding: '8px 11px 8px 30px',
            fontSize: 12.5, fontFamily: 'inherit', outline: 'none', minWidth: 210, color: C.ink,
          }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

function columnsFor(addonMode) {
  return [
    { key: 'name', label: 'Building', align: 'left', width: '34%' },
    {
      key: 'contractShown',
      label: addonMode === 'mech' ? 'Mechanical contract'
        : addonMode === 'insuite' ? 'In-suite contract'
          : 'Contract billed',
      align: 'right',
    },
    addonMode !== 'mech' && { key: 'addonInsuiteShown', label: 'In-suite add-on', align: 'right' },
    addonMode !== 'insuite' && { key: 'addonMech', label: 'Mechanical add-on', align: 'right' },
    { key: 'ratio', label: 'Per $1', align: 'right' },
  ].filter(Boolean);
}

function SiteTable({ rows, sort, onSort, windowKey, addonMode }) {
  const columns = columnsFor(addonMode);
  if (!rows.length) {
    return (
      <div style={{
        background: C.card, border: `1px dashed ${C.line}`, borderRadius: 12,
        padding: '30px 22px', textAlign: 'center', color: C.muted, fontSize: 14,
      }}>
        No buildings match that search.
      </div>
    );
  }

  const toggle = (key) => {
    if (sort.key === key) onSort({ key, dir: sort.dir === 'asc' ? 'desc' : 'asc' });
    else onSort({ key, dir: key === 'name' ? 'asc' : 'desc' });
  };

  return (
    <div style={{
      background: C.card, border: `1px solid ${C.line}`, borderRadius: 12,
      boxShadow: C.shadow, overflow: 'hidden',
    }}>
      <div className="internal-data-scroll" role="region" aria-label="Scrollable data table" tabIndex={0} style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: C.lineSoft }}>
              {columns.map((col) => {
                const active = sort.key === col.key;
                const Icon = !active ? FaSort : sort.dir === 'asc' ? FaSortUp : FaSortDown;
                return (
                  <th
                    key={col.key}
                    onClick={() => toggle(col.key)}
                    style={{
                      textAlign: col.align, padding: '11px 14px', cursor: 'pointer',
                      color: active ? C.navy : C.muted, fontSize: 11.5, fontWeight: 750,
                      textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap',
                      borderBottom: `1px solid ${C.line}`, width: col.width,
                      userSelect: 'none',
                    }}
                  >
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      flexDirection: col.align === 'right' ? 'row-reverse' : 'row',
                    }}>
                      {col.label}
                      <Icon style={{ fontSize: 10, opacity: active ? 1 : .45 }} />
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <SiteRow key={row.siteId} row={row} windowKey={windowKey} addonMode={addonMode} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SiteRow({ row, windowKey, addonMode }) {
  const cell = { padding: '11px 14px', borderBottom: `1px solid ${C.lineSoft}`, whiteSpace: 'nowrap' };
  const num = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  // A high multiple on a tiny contract is noise, not a story. Only
  // highlight the ratio once there's enough contract billing behind it
  // to mean something.
  const ratioMeaningful = row.ratio != null && row.contractShown >= 2000;

  return (
    <tr>
      <td style={{ ...cell, whiteSpace: 'normal' }}>
        <div style={{ fontWeight: 700, color: C.ink }}>{row.name}</div>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 4, alignItems: 'center' }}>
          {(row.contractTypes || []).map((t) => (
            <span key={t} style={{
              background: C.lineSoft, color: C.inkSoft, borderRadius: 5,
              padding: '1px 6px', fontSize: 10.5, fontWeight: 700,
            }}>{CONTRACT_LABELS[t] || t}</span>
          ))}
          {row.buildingCount > 1 && (
            <span style={{
              background: C.blueSoft, color: C.blue, borderRadius: 5,
              padding: '1px 6px', fontSize: 10.5, fontWeight: 700,
            }}>{row.buildingCount} towers combined</span>
          )}
          {!row.hasJobberLink && (
            <span
              title="No Jobber client linked — revenue here was matched by billing address only"
              style={{
                background: C.amberSoft, color: C.amber, borderRadius: 5,
                padding: '1px 6px', fontSize: 10.5, fontWeight: 700,
              }}
            >Address match only</span>
          )}
        </div>
      </td>

      <td style={num}>
        <div style={{ fontWeight: 750, color: row.contractShown > 0 ? C.ink : C.muted }}>
          {row.contractShown > 0 ? money(row.contractShown) : '—'}
        </div>
        {addonMode === 'both' && row.contractMech > 0 && (
          <div style={{ fontSize: 11, color: C.muted }}>
            {money(row.contractMech)} mech
          </div>
        )}
      </td>

      {addonMode !== 'mech' && (
        <td style={{ ...num, color: row.addonInsuiteShown > 0 ? C.green : C.muted, fontWeight: 700 }}>
          {row.addonInsuiteShown > 0 ? money(row.addonInsuiteShown) : '—'}
          {windowKey !== 'all' && row.addonInsuite > row.addonInsuiteShown && (
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>
              {money(row.addonInsuite)} all
            </div>
          )}
        </td>
      )}

      {addonMode !== 'insuite' && (
        <td style={{ ...num, color: row.addonMech > 0 ? C.green : C.muted, fontWeight: 700 }}>
          {row.addonMech > 0 ? money(row.addonMech) : '—'}
        </td>
      )}

      <td style={num}>
        {row.ratio == null ? (
          <span style={{ color: C.muted }}>—</span>
        ) : (
          <span style={{
            background: ratioMeaningful && row.ratio >= 1 ? C.greenSoft : C.lineSoft,
            color: ratioMeaningful && row.ratio >= 1 ? C.green : C.muted,
            borderRadius: 6, padding: '3px 8px', fontWeight: 800, fontSize: 12.5,
          }}>{row.ratio.toFixed(2)}×</span>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

function LoadingState({ warming }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.line}`, borderRadius: 12,
      padding: '56px 24px', textAlign: 'center', color: C.muted, fontSize: 14,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
      boxShadow: C.shadow,
    }}>
      <div aria-hidden style={{
        width: 38, height: 38, borderRadius: '50%',
        border: `3px solid ${C.lineSoft}`, borderTopColor: C.navy,
        animation: 'cr-spin 0.9s linear infinite',
      }} />
      <div style={{ maxWidth: 470 }}>
        <div style={{ color: C.ink, fontWeight: 700, marginBottom: 4 }}>
          {warming ? 'First-time fetch for this period…' : 'Loading contract revenue…'}
        </div>
        <div>
          {warming
            ? 'Reading every invoice in the window from Jobber — this takes two to three minutes the first time, then it\'s cached on the server for everyone.'
            : 'Pulling from Jobber.'}
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div style={{
      background: C.card, border: `1px dashed ${C.line}`, borderRadius: 12,
      padding: '32px 24px', textAlign: 'center', color: C.muted, fontSize: 14,
    }}>
      No contracted buildings with revenue in this window.
    </div>
  );
}

function ErrorBanner({ error }) {
  const copy = {
    warming_timeout: ['Still pulling from Jobber', 'The crawl is taking longer than usual. Give it a moment and hit refresh — it should be ready.'],
    forbidden: ['Not available for your access level', 'Contract revenue is limited to Admin accounts.'],
    no_token: ['Jobber isn\'t connected', 'Reconnect Jobber from the PnL Calendar page, then come back.'],
    no_contracted_buildings: ['No contracts found', 'No active building has a contract recorded, so there\'s nothing to report on.'],
  }[error.kind] || ['Couldn\'t load contract revenue', error.detail || ''];

  return (
    <div style={{
      background: C.amberSoft, border: '1px solid #FCD9B6', color: '#9A3412',
      borderRadius: 9, padding: '11px 15px', marginBottom: 14, fontSize: 13,
    }}>
      <strong>{copy[0]}.</strong> {copy[1]}
    </div>
  );
}
